import { Observable, BehaviorSubject, of as observableOf, of } from 'rxjs';
import { mergeMap, catchError, share, delay, filter, take } from "rxjs/operators";
import { IBaseResponse } from '../ClientService';
import { addProvider, IProviderDescriptor } from "../ServiceUtil";
import { AttendanceDelgate } from "./attendance.delegate";
import {
    IAntipasResponse,
    IAntipasSwipeResponse,
    ISwipeData,
    IAntipasDailySwipeResponse,
    IAntipasDailyAbsentResponse,
    IUnmappedEmployeeResponse,
    IAntipasSyncLogResponse,
    IAntipasRawAttendance,
    IAntipasSyncLog,
    IAntipasAbsentEmp,
    INewEmployeeAbsence,
    IEmployeeAbsence,
    IEmployeeAbsenceHistoryResponse,
    IAccrualHistoryResponse,
    IAbsenceResponse,
    IAccrual,
    IAccuralEmpSummaryRes,
    IAccuralEmpSummary,
    IEmpAccuralHistory,
    IEmpAccuralHistoryRes,
} from "./attendance.interface";

import { AuthenticationService } from "../AuthenticationService";

export class AttendanceService {

    private AntipasData: IAntipasRawAttendance[] = null;
    private AntipasDataSubject: BehaviorSubject<IAntipasRawAttendance[]> = new BehaviorSubject(null);
    public AntipasRawData: Observable<IAntipasRawAttendance[]> = this.AntipasDataSubject;

    private antipasSwipeData: ISwipeData[] = null;
    private antipasSwipeDataSubject: BehaviorSubject<ISwipeData[]> = new BehaviorSubject(null);
    public antipasSwipe: Observable<ISwipeData[]> = this.antipasSwipeDataSubject;

    private antipasUnMappedData: IAntipasRawAttendance[] = null;
    private antipasUnMappedDataSubject: BehaviorSubject<IAntipasRawAttendance[]> = new BehaviorSubject(null);
    public antipasUnMappedEmployees: Observable<IAntipasRawAttendance[]> = this.antipasUnMappedDataSubject;

    private antipasSyncLogData: IAntipasSyncLog[] = null;
    private antipasSyncLogDataSubject: BehaviorSubject<IAntipasSyncLog[]> = new BehaviorSubject(null);
    public antipasSyncLog: Observable<IAntipasSyncLog[]> = this.antipasSyncLogDataSubject;

    private antipasDailySwipeData: ISwipeData[] = null;
    private antipasDailySwipeDataSubject: BehaviorSubject<ISwipeData[]> = new BehaviorSubject(null);
    public antipasDailySwipe: Observable<ISwipeData[]> = this.antipasDailySwipeDataSubject;

    private employeeAbsenceData: IEmployeeAbsence[] = null;
    private employeeAbsenceDataSubject: BehaviorSubject<IEmployeeAbsence[]> = new BehaviorSubject(null);
    public employeeAbsences: Observable<IEmployeeAbsence[]> = this.employeeAbsenceDataSubject;

    private selectedAttendanceData: IEmployeeAbsence = null;
    private selectedAttendanceDataSubject: BehaviorSubject<IEmployeeAbsence> = new BehaviorSubject(this.selectedAttendanceData);
    public selectedAttendance: Observable<IEmployeeAbsence> = this.selectedAttendanceDataSubject.pipe(filter((data) => !!data));

    public managerEmpAbsenceData: IEmployeeAbsence[] = null;
    private managerEmpAbsenceDataSubject: BehaviorSubject<IEmployeeAbsence[]> = new BehaviorSubject(null);
    public managerEmpAbsence: Observable<IEmployeeAbsence[]> = this.managerEmpAbsenceDataSubject;
    private selectedAttendanceId: string = '0';

    private antipasDailyAbsentData: IAntipasAbsentEmp[] = null;
    private antipasDailyAbsentDataSubject: BehaviorSubject<IAntipasAbsentEmp[]> = new BehaviorSubject(null);
    public antipasDailyAbsent: Observable<IAntipasAbsentEmp[]> = this.antipasDailyAbsentDataSubject;

    private accrualHistoryData: IAccrual[] = null;
    private accrualHistoryDataSubject: BehaviorSubject<IAccrual[]> = new BehaviorSubject(null);
    public accrualHistory: Observable<IAccrual[]> = this.accrualHistoryDataSubject;

    private accrualOrgSummaryData: IAccrual[] = null;
    private accrualOrgSummaryDataSubject: BehaviorSubject<IAccrual[]> = new BehaviorSubject(null);
    public accrualOrgSummary: Observable<IAccrual[]> = this.accrualOrgSummaryDataSubject;

    public employeeAbsenceHistoryData: IEmployeeAbsence[] = [];
    public employeeAbsenceHistoryDataSubject = new BehaviorSubject<IEmployeeAbsence[]>([]);
    public employeeAbsenceHistory = this.employeeAbsenceHistoryDataSubject.asObservable();

    private accrualEmpYearSummaryData: IAccuralEmpSummary = null;
    private accrualEmpYearSummaryDataSubject: BehaviorSubject<IAccuralEmpSummary> = new BehaviorSubject(null);
    public accrualEmpYearSummary: Observable<IAccuralEmpSummary> = this.accrualEmpYearSummaryDataSubject;

    private accrualEmpHistoryByRangeData: IEmpAccuralHistory = null;
    private accrualEmpHistoryByRangeDataSubject: BehaviorSubject<IEmpAccuralHistory> = new BehaviorSubject(null);
    public accrualEmpHistoryByRange: Observable<IEmpAccuralHistory> = this.accrualEmpHistoryByRangeDataSubject;


    constructor(private attendanceDelegate: AttendanceDelgate,
        private authenticationService: AuthenticationService) { }

    public get employeeAttendanceId(): string {
        return this.selectedAttendanceId;
    }

    public setSelectedAttendanceId(attendanceId: string): void {
        this.selectedAttendanceId = attendanceId;
    }

    public getAntipasRawData(fromDate: string, toDate: string): Observable<boolean> {
        const response = this.attendanceDelegate.getAntipasRawData(fromDate, toDate);
        response.subscribe(onSuccess.bind(this), onAbsenceFault.bind(this));
        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share()
        );
        function onSuccess(response: IAntipasResponse) {
            this.AntipasData = response.data.attendanceData;
            this.AntipasDataSubject.next(this.AntipasData);
        }
        function onAbsenceFault(error: any) {
            // handle fault
        }
    }
    public getAntipasSwipeData(fromDate: string, toDate: string, employeeId: string): Observable<boolean> {
        const orgId = this.authenticationService.GetOrgId();
        const response = this.attendanceDelegate.getAntipasSwipeData(orgId, fromDate, toDate, employeeId);
        response.subscribe(onSuccess.bind(this), onAbsenceFault.bind(this));
        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share()
        );
        function onSuccess(response: IAntipasSwipeResponse) {
            this.antipasSwipeData = response.data.swipeData;
            this.antipasSwipeDataSubject.next(this.antipasSwipeData);
        }
        function onAbsenceFault(error: any) {
            // handle fault
        }
    }
    public getAntipasDailySwipeData(date: string, employeeIds: string[]): Observable<boolean> {
        const orgId = this.authenticationService.GetOrgId();
        const response = this.attendanceDelegate.getAntipasDailySwipeData(orgId, date, employeeIds);
        response.subscribe(onSuccess.bind(this), onAbsenceFault.bind(this));
        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share()
        );
        function onSuccess(response: IAntipasDailySwipeResponse) {
            this.antipasDailySwipeData = response.data.swipeSummaries;
            this.antipasDailySwipeDataSubject.next(this.antipasDailySwipeData);
        }
        function onAbsenceFault(error: any) {
            // handle fault
        }
    }
    public getAntipasDailyAbsentData(date: string): Observable<boolean> {
        const orgId = this.authenticationService.GetOrgId();
        const response = this.attendanceDelegate.getAntipasDailyAbsentData(orgId, date);
        response.subscribe(onSuccess.bind(this), onAbsenceFault.bind(this));
        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share()
        );
        function onSuccess(response: IAntipasDailyAbsentResponse) {
            this.antipasDailyAbsentData = response.data.absentEmployees;
            this.antipasDailyAbsentDataSubject.next(this.antipasDailyAbsentData);
        }
        function onAbsenceFault(error: any) {
            // handle fault
        }
    }
    public getAntipasUnmappedEmployeeData(): Observable<boolean> {
        const orgId = this.authenticationService.GetOrgId();
        const response = this.attendanceDelegate.getAntipasUnmappedEmployeeData(orgId);
        response.subscribe(onSuccess.bind(this), onAbsenceFault.bind(this));
        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share()
        );
        function onSuccess(response: IUnmappedEmployeeResponse) {
            this.antipasUnMappedData = response.data.unmappedEmployees;
            this.antipasUnMappedDataSubject.next(this.antipasUnMappedData);
        }
        function onAbsenceFault(error: any) {
            // handle fault
        }
    }
    public getAntipasSyncLogData(): Observable<boolean> {
        const orgId = this.authenticationService.GetOrgId();
        const response = this.attendanceDelegate.getAntipasSyncLogData(orgId);
        response.subscribe(onSuccess.bind(this), onAbsenceFault.bind(this));
        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share()
        );
        function onSuccess(response: IAntipasSyncLogResponse) {
            this.antipasSyncLogData = response.data.logs;
            this.antipasSyncLogDataSubject.next(this.antipasSyncLogData);
        }
        function onAbsenceFault(error: any) {
            // handle fault
        }
    }

    public getEmployeeAbsenceTracking(employeeAbsenceTrackingId: string): IEmployeeAbsence {
        return this.managerEmpAbsenceData.find((advanceDataItem: IEmployeeAbsence) =>
            advanceDataItem.employeeAbsenceTrackingId === employeeAbsenceTrackingId);
    }

    public getPendingAbsencesByReportingManagerId(hardReload: boolean = false): Observable<boolean> {

        if (this.employeeAbsenceData !== null && this.employeeAbsenceData.length > 0 && hardReload === false) {
            return observableOf(true);
        }
        var reportingManagerId = this.authenticationService.GetEmployeeId();

        var response = this.attendanceDelegate.getAllAbsencesByReportingManagerId(reportingManagerId);

        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));

        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onEmployeeSuccess(response: IAbsenceResponse) {
            this.employeeAbsenceData = response.attendances;
            this.employeeAbsenceDataSubject.next(this.employeeAbsenceData);
        }

        function onEmployeeFault(error: any) {
            //PayrollService.logger.error(`getEmployeePayroll Fault - Error (${JSON.stringify(error)})`);
        }
    }
    public getEmployeeAbsenceById(employeeAttendanceId: string): Observable<IBaseResponse> {
        if (employeeAttendanceId === "0") {
            this.selectedAttendanceData = null;
            this.selectedAttendanceDataSubject.next(this.selectedAttendanceData);
            return observableOf({
                success: false,
                errorString: "Not Found"
            });
        }
        var response = this.attendanceDelegate.getAttendanceIdById(employeeAttendanceId);
        return this.processEmployeeAttendance(response);
    }
    private processEmployeeAttendance(attendance: Observable<IAbsenceResponse>): Observable<IBaseResponse> {
        attendance.subscribe(onAttendanceSuccess.bind(this), onAttendanceFault.bind(this));
        return attendance.pipe(
            mergeMap((response: IAbsenceResponse) => observableOf({
                success: response.success,
                errorString: response.errorString
            })),
            catchError((response: IAbsenceResponse) => observableOf({
                success: response.success,
                errorString: response.errorString
            })),
            share()
        );

        function onAttendanceSuccess(attendanceResponse: IAbsenceResponse) {
            if (attendanceResponse.success) {
                this.selectedAttendanceId = attendanceResponse.employeeAttendanceId
                    ? attendanceResponse.employeeAttendanceId
                    : this.selectedAttendanceId;
                this.selectedAttendanceData = attendanceResponse.attendance;
                this.selectedAttendanceDataSubject.next(this.selectedAttendanceData);
            }
        }
        function onAttendanceFault(error: any) {
            //  ClientService.logger.error(`processClients Fault - Error (${JSON.stringify(error)})`);
        }
    }
    public createNewEmployeeAbsence(empAbsence: INewEmployeeAbsence): Observable<IBaseResponse> {
        const baseRequest = this.authenticationService.getBaseRequest;
        const payload = {
            ...empAbsence,
            createdBy: baseRequest.userName
        };
        const response = this.attendanceDelegate.createNewEmployeeAbsence(payload);
        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));
        return response.pipe(
            mergeMap((res: IAbsenceResponse) => observableOf({ success: res.success, errorString: res.errorString })),
            catchError((response: IAbsenceResponse) => {
                return of({
                    success: response.success,
                    errorString: response.errorString
                });
            }),
            share());

        function onEmployeeSuccess(response: IAbsenceResponse) {
            if (response.success && response.attendances) {
                const existing = this.employeeAbsenceData || [];
                this.employeeAbsenceData = [...existing, ...response.attendances];
                this.employeeAbsenceDataSubject.next(this.employeeAbsenceData);
            }
        }

        function onEmployeeFault(error: any) {
            //PayrollService.logger.error(`getEmployeePayroll Fault - Error (${JSON.stringify(error)})`);
        }
    }
    public updateEmployeeAbsence(empAbsence: IEmployeeAbsence): Observable<IBaseResponse> {

        const baseRequest = this.authenticationService.getBaseRequest;
        const payload = {
            ...empAbsence,
            modifiedBy: baseRequest.userName
        };
        const response = this.attendanceDelegate.updateEmployeeAbsence(payload);

        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));

        return response.pipe(
            mergeMap((res: IAbsenceResponse) => observableOf({ success: res.success, errorString: res.errorString })),
            catchError((response: IAbsenceResponse) => {
                return of({
                    success: response.success,
                    errorString: response.errorString
                });
            }),
            share());

        function onEmployeeSuccess(clientResponse: IAbsenceResponse) {
            if (clientResponse.success && clientResponse.attendance && this.employeeAbsenceData) {
                const index = this.employeeAbsenceData.findIndex(
                    a => a.employeeAttendanceId === clientResponse.attendance.employeeAttendanceId
                );
                if (index > -1) {
                    this.employeeAbsenceData[index] = clientResponse.attendance;
                } else {
                    this.employeeAbsenceData.push(clientResponse.attendance);
                }
                this.employeeAbsenceDataSubject.next([...this.employeeAbsenceData]);
            }
        }

        function onEmployeeFault(error: any) {
            //PayrollService.logger.error(`getEmployeePayroll Fault - Error (${JSON.stringify(error)})`);
        }
    }
    public getAllEmployeeAbsence(year: any): Observable<boolean> {
        const employeeId = this.authenticationService.GetEmployeeId();
        const response = this.attendanceDelegate.getEmployeeAbsenceById(employeeId, year);

        response.subscribe(
            (res: IAbsenceResponse) => {
                this.employeeAbsenceData = res.attendances;
                this.employeeAbsenceDataSubject.next(this.employeeAbsenceData);
            },
            () => { /* noop */ }
        );

        return response.pipe(
            mergeMap((r: any) => observableOf(!!r)),
            catchError(() => observableOf(false)),
            share()
        );
    }
    public getPendingAbsencesByOrgId(forceRefresh: boolean = false): Observable<boolean> {
        if (!forceRefresh && this.employeeAbsenceData !== null && this.employeeAbsenceData.length > 0) {
            return observableOf(true);
        }
        const orgId = this.authenticationService.GetOrgId();

        var response = this.attendanceDelegate.getPendingAbsencesByOrgId(orgId);

        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));

        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());
        function onEmployeeSuccess(response: IAbsenceResponse) {
            this.employeeAbsenceData = response.attendances;
            this.employeeAbsenceDataSubject.next(this.employeeAbsenceData);
        }
        function onEmployeeFault(error: any) {
            //PayrollService.logger.error(`getEmployeePayroll Fault - Error (${JSON.stringify(error)})`);
        }
    }

    public approveAbsence(attendanceId: string, managerComments: string, applySandwichPenalty: boolean): Observable<IBaseResponse> {
        const baseRequest = this.authenticationService.getBaseRequest;
        const approvedBy = baseRequest.userName ?? '';
        const response = this.attendanceDelegate.approveAbsence(
            attendanceId, managerComments, approvedBy, applySandwichPenalty
        );
        return this.processEmployeeAttendances(response);
    }

    public rejectAbsence(attendanceId: string, managerComments: string): Observable<IBaseResponse> {
        var baseRequest = this.authenticationService.getBaseRequest;
        const rejectedBy = baseRequest.userName ?? '';
        const response = this.attendanceDelegate.rejectAbsence(attendanceId, managerComments, rejectedBy);
        return this.processEmployeeAttendances(response);
    }

    public bulkApproveAbsence(attendanceIds: string[], managerComments: string, applySandwichPenalty: boolean): Observable<IBaseResponse> {
        const baseRequest = this.authenticationService.getBaseRequest;
        const approvedBy = baseRequest.userName ?? '';
        const response = this.attendanceDelegate.bulkApproveAbsence(attendanceIds, managerComments, approvedBy, applySandwichPenalty);
        return this.processEmployeeAttendances(response);
    }
    public bulkRejectAbsence(attendanceIds: string[], managerComments: string): Observable<IBaseResponse> {
        const baseRequest = this.authenticationService.getBaseRequest;
        const rejectedBy = baseRequest.userName ?? '';
        const response = this.attendanceDelegate.bulkRejectAbsence(attendanceIds, managerComments, rejectedBy);
        return this.processEmployeeAttendances(response);
    }
    private processEmployeeAttendances(employeeAbsence: Observable<IAbsenceResponse>): Observable<IBaseResponse> {
        const onSuccess = (clientResponse: IAbsenceResponse) => {
            if (clientResponse.success && clientResponse.attendance && this.employeeAbsenceData) {
                const index = this.employeeAbsenceData.findIndex(
                    a => a.employeeAttendanceId === clientResponse.attendance?.employeeAttendanceId
                );
                if (index > -1) {
                    this.employeeAbsenceData[index] = {
                        ...this.employeeAbsenceData[index],
                        approvalStatus: clientResponse.attendance.approvalStatus
                    };
                    this.employeeAbsenceDataSubject.next([...this.employeeAbsenceData]);
                }
            }
        };
        employeeAbsence.subscribe(onSuccess, () => { });
        return employeeAbsence.pipe(
            mergeMap((response: IAbsenceResponse) => observableOf({
                success: response.success,
                errorString: response.errorString
            })),
            catchError(() => observableOf({ success: false, errorString: 'Error' })),
            share()
        );
    }
    public getAccrualHistoryByEmployee(employeeId: string, year: number): Observable<boolean> {
        var response = this.attendanceDelegate.getAccrualHistoryByEmployee(employeeId, year);
        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));
        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());
        function onEmployeeSuccess(response: IAccrualHistoryResponse) {
            this.accrualHistoryData = response.accruals;
            this.accrualHistoryDataSubject.next(this.accrualHistoryData);
        }
        function onEmployeeFault(error: any) {
            // Handle fault
        }
    }
    public getAccrualOrgSummary(year: number, month: number): Observable<boolean> {
        var orgId = this.authenticationService.GetOrgId();
        var response = this.attendanceDelegate.getAccrualOrgSummary(orgId, year, month);
        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));
        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());
        function onEmployeeSuccess(response: IAccrualHistoryResponse) {
            this.accrualOrgSummaryData = response.accruals;
            this.accrualOrgSummaryDataSubject.next(this.accrualOrgSummaryData);
        }
        function onEmployeeFault(error: any) {
            // Handle fault
        }
    }
    public getEmployeeAbsenceHistoryByDateRange(employeeId: string, fromDate: string, toDate: string): Observable<boolean> {
        const response = this.attendanceDelegate.getEmployeeAbsenceHistoryByDateRange(employeeId, fromDate, toDate);
        response.subscribe(onSuccess.bind(this), onAbsenceFault.bind(this));
        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share()
        );
        function onSuccess(response: IEmployeeAbsenceHistoryResponse) {
            this.employeeAbsenceHistoryData = response.attendances;
            this.employeeAbsenceHistoryDataSubject.next(this.employeeAbsenceHistoryData);
        }
        function onAbsenceFault(error: any) {
            // handle fault
        }
    }
    public getEmpYearSummary(year: number): Observable<boolean> {
        var employeeId = this.authenticationService.GetEmployeeId();
        var response = this.attendanceDelegate.getEmpYearSummary(employeeId, year);
        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));
        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());
        function onEmployeeSuccess(response: IAccuralEmpSummaryRes) {
            this.accrualEmpYearSummaryData = response.summary;
            this.accrualEmpYearSummaryDataSubject.next(this.accrualEmpYearSummaryData);
        }
        function onEmployeeFault(error: any) {
            // Handle fault
        }
    }
    public getEmpAccrualHistoryByDateRange(fromDate: string, toDate: string): Observable<boolean> {
        var employeeId = this.authenticationService.GetEmployeeId();
        var response = this.attendanceDelegate.getEmpAccrualHistoryByDateRange(employeeId, fromDate, toDate);
        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));
        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());
        function onEmployeeSuccess(response: IEmpAccuralHistoryRes) {
            this.accrualEmpHistoryByRangeData = response.history;
            this.accrualEmpHistoryByRangeDataSubject.next(this.accrualEmpHistoryByRangeData);
        }
        function onEmployeeFault(error: any) {
            // Handle fault
        }
    }
}
